"use client";
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { DEFAULT_TZ, orgTodayKey, msUntilMidnight } from "@/lib/timezoneUtils";

type TimezoneContextValue = {
  tz: string;
  todayKey: string;
};

const TimezoneContext = createContext<TimezoneContextValue>({
  tz: DEFAULT_TZ,
  todayKey: orgTodayKey(DEFAULT_TZ),
});

export function TimezoneProvider({ children }: { children: ReactNode }) {
  const [tz, setTz] = useState<string>(DEFAULT_TZ);
  const [todayKey, setTodayKey] = useState<string>(() => orgTodayKey(DEFAULT_TZ));

  // Fetch org timezone from settings on mount
  useEffect(() => {
    fetch("/api/settings/office")
      .then(r => r.json())
      .then(d => {
        const orgTz = d?.timezone ?? DEFAULT_TZ;
        setTz(orgTz);
        setTodayKey(orgTodayKey(orgTz));
      })
      .catch(() => {/* keep default */});
  }, []);

  // Refresh todayKey at midnight in the org timezone
  useEffect(() => {
    const ms = msUntilMidnight(tz);
    const t = setTimeout(() => {
      setTodayKey(orgTodayKey(tz));
    }, ms);
    return () => clearTimeout(t);
  }, [tz, todayKey]);

  return (
    <TimezoneContext.Provider value={{ tz, todayKey }}>
      {children}
    </TimezoneContext.Provider>
  );
}

export function useTimezone(): TimezoneContextValue {
  return useContext(TimezoneContext);
}
